// libs
import React, { memo, useEffect, useState, useMemo } from 'react';
import { SerializedStyles } from '@emotion/core';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// helpers
import { inPriceFormat } from 'shared/helpers/utils';
import { isSaleLive, getEpoch } from 'shared/helpers/saleHelper';

// styles
import {
  Amount,
  Container,
  Currency,
  Discount,
  Label,
  OriginalPrice,
  LineThroughPrice,
  DownArrow,
  UrgencyTag,
  UrgencyContainer,
  MRP,
  Text as GradientText,
  PlainText,
} from './styles';

// constants
import {
  ALL_INCLUSIVE_TEXTS,
  URGENCY_ARROW_COLOR,
  URGENCY_ARROW_SIZE,
  URGENCY_ANIMATION_BUFFER,
  PINK_FRIDAY_PRICE_TEXT,
} from 'shared/layouts/DetailsPage/constants';
import { DEFAULT_CURRENCY } from 'shared/constants';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useSelector } from 'react-redux';
import { getProductOffers } from 'shared/store/details/selectors';
import { getActiveSpecialTagType } from '../../helpers';

// defs

interface UrgencyData {
  text: string | undefined;
  enabled: boolean;
  textColor: string;
}

interface IPriceWithDiscountProps {
  discount?: string | number;
  discountedPrice?: string | number;
  price?: string | number;
  hidePercentage?: boolean;
  cssProps?: SerializedStyles | SerializedStyles[];
  className?: string;
  amountClassName?: string;
  originalAmountClassName?: string;
  inclusiveTextFull?: boolean;
  isStickyPrice?: boolean;
  isOutOfStock?: boolean;
  urgencyData?: UrgencyData;
  PriceChildren?: React.ReactNode;
  isSaleParticipating?: boolean;
  specialTagData?: ProductDetail.ISpecialTag;
}

function PriceWithDiscount({
  discount = 0,
  discountedPrice,
  price,
  hidePercentage = false,
  cssProps,
  className = '',
  amountClassName = '',
  originalAmountClassName = '',
  inclusiveTextFull = true,
  isStickyPrice = false,
  isOutOfStock = false,
  urgencyData,
  PriceChildren,
  isSaleParticipating,
  specialTagData
}: IPriceWithDiscountProps) {
  price = inPriceFormat(price);
  const ArrowRightIcon = useStoreIcon(IconName.ARROW_RIGHT);

  const [priceAnimation, setPriceAnimation] = useState(true);
  const urgencyNudgeEnabled =
    urgencyData?.enabled && isStickyPrice && urgencyData?.text?.length > 0 && !isOutOfStock;
  const showNudgeAnimation = PriceChildren || urgencyNudgeEnabled;
  const nudgeData = urgencyNudgeEnabled ? urgencyData?.text : PriceChildren;
  const {
    enabled: saleExpEnabled,
    saleCommunication,
    theme: saleExpTheme,
    specialTag
  } = useRemoteConfig('saleExperience') || {};

  const { enabled, endDate, pdp: { enabled: isPdpEnabled,
    mrpDpDiscount: { onSaleTag, specialTag: mrpSpecialTag },
    offersBestPrice: { onSaleTag: cartOnSaleTag, specialTag: cartSpecialTag }
  } } = saleCommunication || {};

  const now = Math.floor(Date.now() / 1000);
  const saleLive = isSaleLive(saleCommunication);

  const offersData = useSelector(getProductOffers);
  const { onSale, specialTag: cartOfferSpecialTag } = offersData;

  const extendedDateEnabled = saleCommunication?.dateExtended > endDate;
  const pdpSaleEnabled = !isOutOfStock && isSaleParticipating && saleExpEnabled && saleCommunication.enabled && isPdpEnabled && saleLive;
  const onSaleTagEnabled = pdpSaleEnabled && onSaleTag;

  const salePriceParticipatingEnabled = onSaleTagEnabled && extendedDateEnabled;

  const specialTagStart = getEpoch(specialTagData?.epochStart);
  const specialTagEnd = getEpoch(specialTagData?.epochEnd);

  const specialTagWithPriceParticipatingEnabled = !isOutOfStock && pdpSaleEnabled && extendedDateEnabled &&
    mrpSpecialTag && specialTag?.pdpEnabled &&
    (specialTagData && Object.keys(specialTagData).length > 0 && specialTagData?.type && (specialTagStart < now && specialTagEnd > now) || (specialTagData && (!specialTagStart || !specialTagEnd)));

  const pdpSpecialTag = getActiveSpecialTagType(specialTagData, cartOfferSpecialTag, isSaleParticipating, onSale);

  const isSpecialTagDataEmpty = !specialTagData || Object.keys(specialTagData).length === 0;
  const isCartSpecialTagDataValid = cartOfferSpecialTag && Object.keys(cartOfferSpecialTag).length > 0 && pdpSpecialTag && cartOfferSpecialTag?.type && cartOfferSpecialTag?.type !== null &&
    ((getEpoch(cartOfferSpecialTag?.epochEnd) > now) || (!getEpoch(cartOfferSpecialTag?.epochEnd)));

  const isSpecialTagDataValid = useMemo(() => {
    return Boolean(
      specialTagData &&
      Object.keys(specialTagData).length > 0 &&
      specialTagData?.type &&
      specialTagData?.type !== null &&
      ((getEpoch(specialTagData?.epochEnd) > now) || (!getEpoch(specialTagData?.epochEnd)))
    );
  }, [specialTagData, now]);

  const priceCartRule = saleExpEnabled && (isSaleParticipating && onSale) && enabled && isPdpEnabled && saleLive;
  const priceCartRuleEnabled = !isOutOfStock && priceCartRule && (onSaleTag || cartOnSaleTag);
  const specialTagConfig = (mrpSpecialTag || cartSpecialTag);
  const priceCartSpecialTagEnabled = !isOutOfStock && specialTagConfig && priceCartRule && (isSaleParticipating && (isSpecialTagDataEmpty || !isSpecialTagDataValid) && isCartSpecialTagDataValid);
  const pdpSpecialTagText = specialTag?.tagConfig[pdpSpecialTag]?.text || '';

  useEffect(() => {
    let timer;
    if (!priceAnimation) {
      timer = setTimeout(() => {
        setPriceAnimation(true);
      }, URGENCY_ANIMATION_BUFFER);
    }
    () => {
      clearTimeout(timer);
    };
  }, [priceAnimation]);

  const urgencytag = useMemo(() => {
    return (
      <UrgencyTag
        color={urgencyData?.textColor}
        priceAnimation={priceAnimation}
        onAnimationEnd={() => setPriceAnimation(false)}
      >
        {nudgeData}
      </UrgencyTag>
    );
  }, [nudgeData, priceAnimation, urgencyData?.textColor]);

  const labelWithDiscount = useMemo(() => {
    return (
      <Label className="mrp-label">
        <MRP>{ALL_INCLUSIVE_TEXTS.MRP}</MRP>
        <OriginalPrice className={originalAmountClassName} data-at="mrp-pdp">
          <Currency>{DEFAULT_CURRENCY}</Currency>
          <LineThroughPrice>{price}</LineThroughPrice>
        </OriginalPrice>
        <span className="sub" data-test="all-inclusive">
          {inclusiveTextFull ? ALL_INCLUSIVE_TEXTS.INCLUSIVE : ALL_INCLUSIVE_TEXTS.INCLUSIVE_HALF}
        </span>
      </Label>
    );
  }, [inclusiveTextFull, originalAmountClassName, price]);

  const labelWithoutDiscount = useMemo(() => {
    return (
      <Label className="price-label">
        <MRP>{ALL_INCLUSIVE_TEXTS.MRP}</MRP>
        <span className="sub" data-test="all-inclusive">
          {inclusiveTextFull ? ALL_INCLUSIVE_TEXTS.INCLUSIVE : ALL_INCLUSIVE_TEXTS.INCLUSIVE_HALF}
        </span>
      </Label>
    );
  }, [inclusiveTextFull]);

  // SALE_EXPERIENCE
  const saleExpText = useMemo(() => {
    if (priceCartSpecialTagEnabled) {
      return null;
    }
    if (pdpSpecialTagText && (specialTagWithPriceParticipatingEnabled)) {
      return <PlainText>{specialTag.tagConfig[pdpSpecialTag]?.text}</PlainText>;
    } else if (salePriceParticipatingEnabled || priceCartRuleEnabled) {
      return (
        <GradientText gradientColors={saleExpTheme.textGradient}>
          {saleCommunication?.pdp?.stickyPriceTag || PINK_FRIDAY_PRICE_TEXT}
        </GradientText>
      );
    }
  }, [priceCartSpecialTagEnabled, specialTagWithPriceParticipatingEnabled, salePriceParticipatingEnabled, priceCartRuleEnabled, pdpSpecialTag, pdpSpecialTagText, saleCommunication?.pdp?.stickyPriceTag, saleExpTheme?.textGradient, specialTag?.tagConfig]);

  return discount > 0 ? (
    <Container className={className} cssProps={cssProps} data-test="has-discount">
      {saleExpText}
      <div>
        <Amount className={amountClassName} data-at="sp-pdp">
          <Currency>{DEFAULT_CURRENCY}</Currency>
          {inPriceFormat(discountedPrice)}
        </Amount>
        {urgencyNudgeEnabled && <DownArrow as={ArrowRightIcon} size={URGENCY_ARROW_SIZE} color={URGENCY_ARROW_COLOR} />}
        {hidePercentage === false && (
          <Discount
            data-test="discount-percentage"
            className="discountPercentage"
            data-at="price-offer"
          >
            {discount}% Off
          </Discount>
        )}
      </div>
      {priceAnimation && showNudgeAnimation ? (
        <UrgencyContainer className="urgency-nudge">
          {urgencytag}
          {labelWithDiscount}
        </UrgencyContainer>
      ) : (
        <>{labelWithDiscount}</>
      )}
    </Container>
  ) : (
    <Container className={className} cssProps={cssProps} data-test="no-discount">
      {saleExpText}
      <Amount>
        <Currency>{DEFAULT_CURRENCY}</Currency>
        {price}
        {urgencyNudgeEnabled && <DownArrow as={ArrowRightIcon} size={URGENCY_ARROW_SIZE} color={URGENCY_ARROW_COLOR} />}
      </Amount>

      {priceAnimation && showNudgeAnimation ? (
        <UrgencyContainer className="urgency-nudge">
          {urgencytag}
          {labelWithoutDiscount}
        </UrgencyContainer>
      ) : (
        <>{labelWithoutDiscount}</>
      )}
    </Container>
  );
}

export default memo(PriceWithDiscount);
